import { Column, GridApi } from 'ag-grid-community';
import { CustomHeaderProps } from 'ag-grid-react';
import {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useBomPartsImportContext } from 'bom/states';
import { IImportColumnMapping } from 'bom/types';
import { getImportHeaderLabels } from 'bom/utils';
import { CheckboxInput, Select } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { TextEllipsis } from 'core/elements';
import { useClickOutside } from 'core/hooks';
import { Color, Level } from 'theme/enums';

import { Divider, HeaderName, HeaderWrapper } from './style';

const updateColumnMapping = (
  field: string | null,
  isMapped: boolean,
  colId: number,
  setColumnsMapping: Dispatch<SetStateAction<IImportColumnMapping[]>>
) => {
  setColumnsMapping((prev) =>
    prev.map((column) =>
      column.colIndex === colId
        ? {
            ...column,
            field: field || column.initialField || `col${column.colIndex}`,
            isMapped,
          }
        : column
    )
  );
};

const updateColumnCellsStyle = (api: GridApi, column: Column, isMapped = false) => {
  if (!column) {
    return;
  }

  const colId = column.getColId();
  const colDef = column.getColDef();

  const updatedColDef = {
    ...colDef,
    cellStyle: { ...colDef.cellStyle, opacity: isMapped ? 1 : 0.5 },
  };

  const allColumnDefs = api.getColumns().map((col) => {
    if (col.getColId() === colId) {
      return updatedColDef;
    }

    return col.getColDef();
  });

  api.setGridOption('columnDefs', allColumnDefs);
};

const HeaderComponent = ({ api, column }: CustomHeaderProps) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const { headerRow, columnsMapping, setColumnsMapping, type } = useBomPartsImportContext();

  const colId = Number(column?.getColId());

  const associatedColumMapping = columnsMapping?.[colId];

  const importHeaderLabels = getImportHeaderLabels(type);

  let displayValue: string = (headerRow?.[colId] as string) || '';

  if (associatedColumMapping?.isMapped) {
    displayValue = importHeaderLabels?.[associatedColumMapping?.field];
  }

  const [headerChecked, setHeaderChecked] = useState(false);
  const [selectValue, setSelectValue] = useState<string | null>(null);

  useClickOutside(ref, () => setHeaderChecked(headerChecked && Boolean(selectValue)));

  useEffect(() => {
    setSelectValue(associatedColumMapping?.isMapped ? associatedColumMapping?.field : null);
    setHeaderChecked(associatedColumMapping?.isMapped);

    updateColumnCellsStyle(api, column, associatedColumMapping?.isMapped);
  }, [api, associatedColumMapping, column]);

  const handleCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setSelectValue(null);
      setHeaderChecked(false);

      updateColumnMapping(null, false, colId, setColumnsMapping);
      updateColumnCellsStyle(api, column, false);

      return;
    }

    setHeaderChecked(true);
  };

  const handleSelectChange = (_: KeyboardEvent<HTMLInputElement>, value: string) => {
    setSelectValue(value);

    updateColumnMapping(value, true, colId, setColumnsMapping);
    updateColumnCellsStyle(api, column, true);
  };

  return (
    <HeaderWrapper ref={ref} spacing={2}>
      <HeaderName level={Level.TITLE_SM}>
        <CheckboxInput
          value={headerChecked}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleCheckboxChange(event.target.checked)
          }
        />
        <TextEllipsis
          content={displayValue || DEFAULT_PLACEHOLDER}
          color={headerChecked && Boolean(selectValue) ? Color.PRIMARY : undefined}
          onClick={() => handleCheckboxChange(!headerChecked)}
          sx={{ cursor: 'pointer' }}
        />
      </HeaderName>

      <Divider />

      <Select
        key={selectValue}
        value={selectValue ?? undefined}
        placeholder={t('placeholderSelectColumnHeader')}
        onChange={handleSelectChange}
        options={Object.entries(importHeaderLabels).map(([value, label]) => ({
          value,
          label,
          hide: columnsMapping?.some((column) => column.field === value && column.isMapped),
        }))}
        disabled={!headerChecked}
      />
    </HeaderWrapper>
  );
};

export default HeaderComponent;
